import React from "react";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { StyledTableRow } from "../../../../../styles/Shipmentstyles";
import { InventoryCountImportLog } from "../../../../../models/inventory/InventoryAdmin";

interface InventoryCountImportLogsTableProps {
  maxHeight: number;
  logs: InventoryCountImportLog[];
  limit: number;
  page_no: number;
  total_count: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
}

const InventoryCountImportLogsTable = ({
  maxHeight,
  logs,
  page_no,
  limit,
  total_count,
  onPageChange,
  onRowsPerPageChange,
}: InventoryCountImportLogsTableProps) => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleOpenSheet = (url: string) => {
    try {
      if (!url?.trim()) return;
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    } catch (error) {
      console.error("Failed to open count import sheet:", error);
    }
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <Box>
        <TableContainer sx={{ maxHeight }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.customColors?.tableGrayHeadingColor,
                }}
              >
                <AppTableHeaderCell fontSize="12px" minWidth="70px">
                  ID
                </AppTableHeaderCell>
                <AppTableHeaderCell fontSize="12px" minWidth="140px">
                  CUSTOMER
                </AppTableHeaderCell>
                <AppTableHeaderCell fontSize="12px" minWidth="110px">
                  INVENTORY ID
                </AppTableHeaderCell>
                <AppTableHeaderCell fontSize="12px" minWidth="140px">
                  ADMIN
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  fontSize="12px"
                  minWidth="90px"
                  txtAlign="right"
                >
                  ROWS
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  fontSize="12px"
                  minWidth="110px"
                  txtAlign="right"
                >
                  UPDATED
                </AppTableHeaderCell>
                <AppTableHeaderCell fontSize="12px" minWidth="160px">
                  CREATED
                </AppTableHeaderCell>
                <AppTableHeaderCell fontSize="12px" minWidth="80px">
                  SHEET
                </AppTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <StyledTableRow key={log.id}>
                  <AppTableRowCell minWidth="70px">
                    <CustomTypography variant="body2">{log.id}</CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell minWidth="140px" textAlign="left">
                    <CustomTypography variant="body2" fontWeight={600}>
                      {log.customer || "—"}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell minWidth="110px">
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      color={theme.palette.customColors?.purple}
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => {
                        navigate(
                          `/inventory/${log.inventory_id}/packs?customer_id=${log.customer_id}`,
                        );
                      }}
                    >
                      {log.inventory_id}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell minWidth="140px" textAlign="left">
                    <CustomTypography variant="body2">
                      {log.admin_name || "—"}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell minWidth="90px" sx={{ textAlign: "right" }}>
                    <CustomTypography variant="body2">
                      {log.row_count ?? 0}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell minWidth="110px" sx={{ textAlign: "right" }}>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {log.updated_rows_count ?? 0}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell minWidth="160px">
                    <CustomTypography variant="body2">
                      {formatDate(log.created_date)}
                    </CustomTypography>
                  </AppTableRowCell>
                  <AppTableRowCell minWidth="80px">
                    {log.sheet_url ? (
                      <Tooltip title="Open sheet">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSheet(log.sheet_url)}
                          sx={{
                            color: theme.palette.customColors?.purple,
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <CustomTypography variant="body2">—</CustomTypography>
                    )}
                  </AppTableRowCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <AppTablePagenation
          totalCount={total_count}
          limit={limit}
          pageNo={page_no}
          onPageChange={onPageChange}
          handleRowsChange={handleRowsChange}
        />
      </Box>
    </Paper>
  );
};

export default InventoryCountImportLogsTable;
